import { QueryClient, type QueryFunction } from "@tanstack/react-query";
import { api } from "./api";

export function isGuestMode(): boolean {
  return sessionStorage.getItem("trybeup_guest") === "true";
}

const defaultQueryFn: QueryFunction = async ({ queryKey }) => {
  const url = queryKey[0] as string;
  const { data } = await api.get(url);
  return data;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: defaultQueryFn,
      refetchInterval: false,
      // OFF. Was `true` (2026-06-13) to give "same-session cross-device sync"
      // — switching from the desktop tab to the phone re-fired every active
      // query so fresh data arrived without a manual reload. The cost was
      // brutal on mobile: every alt-tab / lock-screen unlock / push-notification
      // glance kicked off 15+ parallel requests on the home tab and the user
      // watched spinners spin (founder report 2026-06-14, "loading times are
      // still slow"). The cross-device sync we gain isn't worth that, and the
      // 60s staleTime + refetchOnMount still pick up fresh data the moment the
      // user navigates anywhere inside the app.
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,       // 1 minute — queries are considered fresh
      gcTime: 30 * 60 * 1000,     // 30 minutes — unused cache entries are garbage collected
      // Retry transient failures on flaky mobile networks (max 3 attempts
      // total: original + 2 retries). Only retry on network errors or 5xx
      // server errors — 4xx (401 Unauthorized, 403 Forbidden, 404 Not
      // Found, 422 Unprocessable) are real client-side errors that should
      // surface immediately, not be hammered. Without this, every brief
      // server hiccup or phone-handing-over-the-tunnel produced a visible
      // error toast on mobile.
      retry: (failureCount, error) => {
        if (failureCount >= 2) return false;
        // Axios attaches the response on err.response; fetch-thrown errors
        // are typeof TypeError. Treat both anything-without-a-status and
        // any 5xx as transient.
        const e = error as { response?: { status?: number }; status?: number };
        const status = e?.response?.status ?? e?.status;
        if (!status) return true;        // network error — retry
        return status >= 500 && status < 600;  // server error — retry
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),  // 1s → 2s → 4s
    },
    mutations: {
      // Mutations stay at retry:false. Auto-retrying a non-idempotent POST
      // (create task / habit / etc.) could double-create resources. The
      // user retries manually if a mutation fails.
      retry: false,
    },
  },
});

/**
 * Legacy apiRequest — kept for compatibility with client code that uses it.
 * Client code does: const res = await apiRequest("POST", "/api/...", data)
 * Then: const result = await res.json()
 */
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown
): Promise<Response> {
  const { apiFetch } = await import("./api");
  const options: RequestInit = {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
  };
  return apiFetch(url, options);
}

/**
 * Compatibility: client code may import getQueryFn from queryClient.
 * Our defaultQueryFn already returns null on 401, so this is a passthrough.
 */
export function getQueryFn(_options?: { on401?: string }) {
  return defaultQueryFn;
}
